import { compareDecimal, parseWorldTerm, type WorldTerm } from "./world";

export const HISTORY_PAGE_SIZE = 512n;
export const HISTORY_WINDOW_PAGES = 5;

const DECIMAL_PATTERN = /^(0|[1-9][0-9]*)$/;

export const HISTORY_SEED_TERM: WorldTerm = {
  n: "0",
  previous: "0",
  value: "0",
  step: "0",
  operation: "add",
};

export interface HistoryPayload {
  from: string;
  through: string;
  nextFrom: string | null;
  terms: WorldTerm[];
}

export interface HistoryPage {
  start: string;
  terms: WorldTerm[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readDecimal(value: unknown, field: string): string {
  if (typeof value !== "string" || !DECIMAL_PATTERN.test(value)) {
    throw new TypeError(`${field} must be a canonical decimal string`);
  }
  return value;
}

export function parseHistoryPayload(value: unknown): HistoryPayload {
  if (!isRecord(value) || !Array.isArray(value.terms)) {
    throw new TypeError("history response must contain a terms array");
  }

  const from = readDecimal(value.from, "history.from");
  const through = readDecimal(value.through, "history.through");
  const nextFrom = value.nextFrom === null
    ? null
    : readDecimal(value.nextFrom, "history.nextFrom");
  const terms = value.terms.map(parseWorldTerm);

  for (let index = 0; index < terms.length; index += 1) {
    const term = terms[index];
    if (!term) continue;
    const expected = BigInt(from) + BigInt(index);
    if (BigInt(term.n) !== expected || BigInt(term.n) > BigInt(through)) {
      throw new TypeError("history response must be contiguous and within through");
    }
  }

  return { from, through, nextFrom, terms };
}

export function historyPageStart(n: bigint): bigint {
  if (n < 0n) throw new RangeError("history index must not be negative");
  if (n === 0n) return 1n;
  return ((n - 1n) / HISTORY_PAGE_SIZE) * HISTORY_PAGE_SIZE + 1n;
}

export function previousHistoryPageStart(start: bigint): bigint | null {
  if (start <= 1n) return null;
  return start - HISTORY_PAGE_SIZE;
}

export function nextHistoryPageStart(start: bigint, through: bigint): bigint | null {
  const next = start + HISTORY_PAGE_SIZE;
  return next <= through ? next : null;
}

export function trimHistoryPages(
  pages: ReadonlyMap<string, HistoryPage>,
  focusStart: bigint,
  maximum = HISTORY_WINDOW_PAGES,
): Map<string, HistoryPage> {
  if (!Number.isSafeInteger(maximum) || maximum < 1) {
    throw new RangeError("maximum pages must be a positive safe integer");
  }

  const focus = pages.get(focusStart.toString());
  if (!focus) return new Map();

  const selected = new Map<string, HistoryPage>([[focus.start, focus]]);
  let lowerOpen = true;
  let upperOpen = true;
  for (let distance = HISTORY_PAGE_SIZE; selected.size < maximum && (lowerOpen || upperOpen); distance += HISTORY_PAGE_SIZE) {
    if (lowerOpen) {
      const lowerStart = focusStart - distance;
      const lower = lowerStart >= 1n ? pages.get(lowerStart.toString()) : undefined;
      if (lower) selected.set(lower.start, lower);
      else lowerOpen = false;
    }
    if (selected.size >= maximum) break;
    if (upperOpen) {
      const upper = pages.get((focusStart + distance).toString());
      if (upper) selected.set(upper.start, upper);
      else upperOpen = false;
    }
  }

  return selected;
}

export function collectHistoryTerms(
  pages: ReadonlyMap<string, HistoryPage>,
  through: bigint,
  liveTerms: readonly WorldTerm[],
  includeLiveTerms = false,
): WorldTerm[] {
  const byN = new Map<string, WorldTerm>();
  for (const page of pages.values()) {
    for (const term of page.terms) {
      if (BigInt(term.n) <= through) byN.set(term.n, term);
    }
  }

  const latestStart = historyPageStart(through).toString();
  if (includeLiveTerms || pages.size === 0 || pages.has(latestStart)) {
    for (const term of liveTerms) {
      if (BigInt(term.n) <= through && historyPageStart(BigInt(term.n)).toString() === latestStart) {
        byN.set(term.n, term);
      }
    }
  }

  if (pages.has("1")) byN.set("0", HISTORY_SEED_TERM);
  return [...byN.values()].sort((left, right) => compareDecimal(right.n, left.n));
}

export function parseHistoryIndexInput(value: string): bigint | null {
  const normalized = value.replace(/[\s,]/g, "");
  if (!/^[0-9]+$/.test(normalized)) return null;
  return BigInt(normalized);
}

export function historyAriaRowCount(through: bigint): number {
  return safeAriaInteger(through + 2n);
}

export function historyAriaRowIndex(through: bigint, n: bigint): number {
  return safeAriaInteger(through - n + 2n);
}

function safeAriaInteger(value: bigint): number {
  return value <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(value) : -1;
}
